import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SafeHtml } from '@angular/platform-browser';
import { Song } from '../../entities/song.model';
import { SongComment } from '../../entities/comment.model';
import { CommentService } from '../../services/comment.service';
import { SongService } from '../../services/song.service';
import { LyricsService } from '../../services/lyrics.service';
import { TranscriptService } from '../../services/transcript.service';

type ModalTab = 'notes' | 'comments' | 'lyrics';

@Component({
  selector: 'app-song-notes-modal',
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, TranslateModule],
  templateUrl: './song-notes-modal.component.html',
  styleUrls: ['./song-notes-modal.component.scss'],
})
export class SongNotesModalComponent implements OnInit, OnDestroy {
  @Input() song!: Song;
  @Input() renderedNote: SafeHtml = '';
  @Input() initialTab: ModalTab = 'notes';
  @Input() canComment = false;
  @Input() currentUserUid = '';
  @Input() currentUserDisplayName = '';
  @Input() nativeLanguage = 'en';
  @Input() onCommentCountChange?: (delta: number) => void;

  @ViewChild('ytPlayerContainer') ytPlayerContainer?: ElementRef<HTMLDivElement>;

  activeSegment: ModalTab = 'notes';

  // Comments state
  comments: SongComment[] = [];
  commentsLoading = false;
  newCommentText = '';
  editingCommentId: string | null = null;
  editingCommentText = '';

  // Lyrics state
  lyricsLoading = false;
  lyricsError = '';
  translationsVisible = false;
  translations: string[] = [];
  translating = false;
  activeSegmentIndex = -1;
  private translationsLoaded = false;

  // YouTube player
  private ytPlayer: any = null;
  private pollInterval: any = null;
  private ytPlayerInitialised = false;

  constructor(
    private modalController: ModalController,
    private commentService: CommentService,
    private songService: SongService,
    private lyricsService: LyricsService,
    private transcriptService: TranscriptService
  ) {}

  async ngOnInit() {
    this.activeSegment = this.initialTab;
    if (this.initialTab === 'comments') {
      await this.loadComments();
    }
    if (this.initialTab === 'lyrics') {
      await this.ensureLyrics();
      if (this.hasLyrics) {
        setTimeout(() => this.initYouTubePlayer(), 150);
      }
    }
  }

  ngOnDestroy() {
    this.stopPolling();
    try { this.ytPlayer?.destroy(); } catch { /* ignore */ }
  }

  async onSegmentChange(event: any) {
    const prev = this.activeSegment;
    this.activeSegment = event.detail.value as ModalTab;

    // Destroy player when leaving Lyrics so no iframe lingers in the background
    if (prev === 'lyrics' && this.activeSegment !== 'lyrics') {
      this.stopPolling();
      try { this.ytPlayer?.destroy(); } catch { /* ignore */ }
      this.ytPlayer = null;
      this.ytPlayerInitialised = false;
    }

    if (this.activeSegment === 'comments' && this.comments.length === 0 && !this.commentsLoading) {
      await this.loadComments();
    }
    if (this.activeSegment === 'lyrics') {
      await this.ensureLyrics();
      // ViewChild is only available after *ngIf renders the container
      if (this.hasLyrics) {
        setTimeout(() => this.initYouTubePlayer(), 80);
      }
    }
  }

  // ── Lyrics ────────────────────────────────────────────────────────────────

  /**
   * Ensures lyrics are loaded. Flow:
   * 1. Already have segments → done.
   * 2. Check Firestore for segments (another session may have fetched them).
   * 3. If still none, try to auto-fetch via Cloud Function (uses TranscriptAPI credits).
   * 4. If that fails, surface a user-friendly error.
   */
  private async ensureLyrics() {
    if (this.song.transcriptSegments?.length || this.lyricsLoading) return;
    if (!this.song.id) return;

    this.lyricsLoading = true;
    this.lyricsError = '';
    try {
      // Step 1: check Firestore for existing segments
      const fresh = await this.songService.getSong(this.song.id);
      if (fresh?.transcriptSegments?.length) {
        this.song = { ...this.song, transcriptSegments: fresh.transcriptSegments };
        return;
      }

      // Step 2: try to auto-fetch via Cloud Function
      try {
        const result = await this.transcriptService.fetchTranscript(
          this.song.id,
          this.song.youtubeUrl,
          this.song.language
        );
        if (result.segments?.length) {
          this.song = { ...this.song, transcriptSegments: result.segments };
        } else {
          this.lyricsError = 'No captions available for this video.';
        }
      } catch (fetchErr: any) {
        const msg: string = fetchErr?.message ?? '';
        if (msg.includes('not-found') || msg.includes('No captions')) {
          this.lyricsError = 'No captions available for this video.';
        } else if (msg.includes('permission-denied') || msg.includes('unauthenticated')) {
          this.lyricsError = 'Captions not fetched yet. Ask the song submitter to fetch them.';
        } else {
          this.lyricsError = 'Could not load captions. Try again later.';
        }
      }
    } finally {
      this.lyricsLoading = false;
      if (this.hasLyrics && !this.ytPlayerInitialised) {
        setTimeout(() => this.initYouTubePlayer(), 80);
      }
    }
  }

  get hasLyrics(): boolean {
    return !!this.song.transcriptSegments?.length;
  }

  formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  seekToTime(start: number) {
    if (this.ytPlayer?.seekTo) {
      this.ytPlayer.seekTo(start, true);
      this.ytPlayer.playVideo?.();
    }
  }

  async toggleTranslations() {
    this.translationsVisible = !this.translationsVisible;
    if (this.translationsVisible && !this.translationsLoaded) {
      await this.loadTranslations();
    }
  }

  private async loadTranslations() {
    const segs = this.song.transcriptSegments;
    if (!segs?.length) return;

    const fromLang = this.song.language || 'auto';
    const toLang = this.nativeLanguage || 'en';
    const langKey = `${fromLang}_${toLang}`;

    // Use cached translations if already on the song object
    const cached = this.song.transcriptTranslations?.[langKey];
    if (cached?.length) {
      this.translations = cached;
      this.translationsLoaded = true;
      return;
    }

    this.translating = true;
    try {
      const lines = segs.map(s => s.text);
      const translations = await this.lyricsService.translateLines(lines, fromLang, toLang);
      this.translations = translations;
      this.translationsLoaded = true;

      // Persist to Firestore so future opens skip Gemini entirely
      if (this.song.id) {
        this.songService.saveTranslations(this.song.id, langKey, translations).catch(console.error);
        const existing = this.song.transcriptTranslations ?? {};
        this.song = { ...this.song, transcriptTranslations: { ...existing, [langKey]: translations } };
      }
    } catch (err) {
      console.error('Translation failed:', err);
      this.translationsVisible = false;
    } finally {
      this.translating = false;
    }
  }

  // ── YouTube Player ────────────────────────────────────────────────────────

  private async initYouTubePlayer() {
    if (this.ytPlayerInitialised || !this.ytPlayerContainer?.nativeElement) return;
    if (!this.song?.youtubeUrl) return;

    const videoId = this.song.youtubeUrl.match(
      /(?:v=|youtu\.be\/|embed\/|shorts\/)([a-zA-Z0-9_-]{11})/
    )?.[1];
    if (!videoId) return;

    this.ytPlayerInitialised = true;

    await this.loadYouTubeApi();

    const YT = (window as any)['YT'];
    this.ytPlayer = new YT.Player(this.ytPlayerContainer.nativeElement, {
      height: '100%',
      width: '100%',
      videoId,
      playerVars: { playsinline: 1, rel: 0, modestbranding: 1 },
      events: {
        onStateChange: (e: any) => {
          if (e.data === YT.PlayerState.PLAYING) {
            this.startPolling();
          } else {
            this.stopPolling();
            if (e.data === YT.PlayerState.ENDED) {
              this.activeSegmentIndex = -1;
            }
          }
        },
      },
    });
  }

  private loadYouTubeApi(): Promise<void> {
    if ((window as any)['YT']?.Player) return Promise.resolve();
    return new Promise(resolve => {
      const prev = (window as any)['onYouTubeIframeAPIReady'];
      (window as any)['onYouTubeIframeAPIReady'] = () => {
        if (prev) prev();
        resolve();
      };
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
    });
  }

  private startPolling() {
    if (this.pollInterval) return;
    this.pollInterval = setInterval(() => {
      const t = this.ytPlayer?.getCurrentTime?.();
      if (typeof t === 'number') this.updateActiveSegment(t);
    }, 250);
  }

  private stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  private updateActiveSegment(currentTime: number) {
    const segs = this.song.transcriptSegments;
    if (!segs?.length) return;

    let idx = -1;
    for (let i = 0; i < segs.length; i++) {
      if (segs[i].start <= currentTime) idx = i;
      else break;
    }

    if (idx !== this.activeSegmentIndex) {
      this.activeSegmentIndex = idx;
      // Scroll the active line into view within the lyrics list
      setTimeout(() => {
        document.querySelector('.lyric-line.active')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
    }
  }

  // ── Comments ──────────────────────────────────────────────────────────────

  private async loadComments() {
    if (!this.song?.id) return;
    this.commentsLoading = true;
    try {
      this.comments = await this.commentService.getComments(this.song.id);
    } finally {
      this.commentsLoading = false;
    }
  }

  async submitComment() {
    const text = this.newCommentText.trim();
    if (!text || !this.currentUserUid || !this.song?.id) return;
    await this.commentService.addComment(
      this.song.id,
      this.currentUserUid,
      this.currentUserDisplayName || 'User',
      text
    );
    this.songService.incrementCommentCount(this.song.id).catch(console.error);
    this.onCommentCountChange?.(1);
    this.newCommentText = '';
    await this.loadComments();
  }

  startEditComment(comment: SongComment) {
    this.editingCommentId = comment.id!;
    this.editingCommentText = comment.text;
  }

  cancelEditComment() {
    this.editingCommentId = null;
    this.editingCommentText = '';
  }

  async saveEditComment() {
    const text = this.editingCommentText.trim();
    if (!text || !this.editingCommentId || !this.song?.id) return;
    await this.commentService.updateComment(this.editingCommentId, text);
    this.editingCommentId = null;
    this.editingCommentText = '';
    await this.loadComments();
  }

  async deleteComment(comment: SongComment) {
    if (!this.song?.id) return;
    await this.commentService.deleteComment(comment.id!);
    this.songService.decrementCommentCount(this.song.id).catch(console.error);
    this.onCommentCountChange?.(-1);
    await this.loadComments();
  }

  dismiss() {
    this.stopPolling();
    try { this.ytPlayer?.stopVideo?.(); } catch { /* ignore */ }
    this.modalController.dismiss();
  }
}
